import { useContext } from 'react'

import {
  useForm, UseFormOptions, UseFormPayload, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import defaultState, { requiredFields } from '@models/agencyPortal'

import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as agencyPortalActions from '@redux/modules/agencyPortal'
import type { AppDispatch } from '@redux/store'
import { DeleteParams } from '@redux/modules/module'

import type {
  AgencyModel, AgencyPortalModel, AgencyPortalRequestOptions, PortalModel,
} from '@models/types'

type CreateAgencyPortalParams = {
  agencyPortalParams: Partial<AgencyPortalModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyPortalRequestOptions,
}

const createAgencyPortal = (params: CreateAgencyPortalParams) => {
  const { agencyPortalParams, dispatch, requestOptions } = params
  const { createAgencyPortal: createFn } = agencyPortalActions

  const stringifyObjectKeys = ['options']
  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects({}, agencyPortalParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

type DeleteAgencyPortalParams = {
  agencyPortal: DeleteParams<AgencyPortalModel>,
  dispatch: AppDispatch,
}

const deleteAgencyPortal = (params: DeleteAgencyPortalParams) => {
  const { agencyPortal, dispatch } = params
  const { deleteAgencyPortal: deleteFn } = agencyPortalActions

  return dispatch(deleteFn(agencyPortal))
}

type GenerateAuthTokenParams = {
  agencyPortal: AgencyPortalModel,
  dispatch: AppDispatch,
}

const generateAuthToken = (params: GenerateAuthTokenParams) => {
  const { agencyPortal, dispatch } = params
  const { generateAuthToken: generateFn } = agencyPortalActions

  return dispatch(generateFn(agencyPortal, { include_agency_portal_auth_token: true }))
}

type UpdateAgencyPortalParams = {
  agencyPortal: AgencyPortalModel,
  agencyPortalParams: Partial<AgencyPortalModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyPortalRequestOptions,
}

const updateAgencyPortal = (params: UpdateAgencyPortalParams) => {
  const {
    agencyPortal, agencyPortalParams, dispatch, requestOptions,
  } = params
  const { updateAgencyPortal: updateFn } = agencyPortalActions

  const stringifyObjectKeys = ['options']
  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects(agencyPortal, agencyPortalParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

export function useAgencyPortalForm(
  agencyPortal: Partial<AgencyPortalModel> = {},
  options: UseFormOptions = {},
): UseFormPayload {
  const { validateOn } = options

  const agencyPortalForm = useForm(
    defaultState,
    { entity: agencyPortal, requiredFields, validateOn },
    [agencyPortal.id, agencyPortal.cache_key],
  )

  return {
    ...agencyPortalForm,
  }
}

export function useRelations(agencyPortal: Partial<AgencyPortalModel>) {
  const { agency_id, portal_id } = agencyPortal || {}

  const entities = useSelector(reduxState => reduxState.entities)
  const { agencies, portals } = entities

  const agency: Partial<AgencyModel> = agency_id ? agencies[agency_id] || {} : {}
  const portal: Partial<PortalModel> = portal_id ? portals[portal_id] || {} : {}

  return {
    agency,
    portal,
  }
}

const useAgencyPortal = (initEntity: Partial<AgencyPortalModel> = {}) => {
  // @ts-expect-error TS2322
  const { entity: agencyPortal }: { entity: AgencyPortalModel } = useLatestEntity(initEntity, 'agencyPortals')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency, portal } = useRelations(agencyPortal)

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.agencyPortals)

  return {
    agency,
    agencyPortal,
    callbacks: {
      createAgencyPortal: (agencyPortalParams: AgencyPortalModel, requestOptions?: AgencyPortalRequestOptions) => (
        createAgencyPortal({ agencyPortalParams, dispatch, requestOptions })
      ),
      createOrEditAgencyPortal: (customPayload: object) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAgencyPortalModal',
        payload: { agencyPortal, ...customPayload },
      }),
      deleteAgencyPortal: () => deleteAgencyPortal({ agencyPortal, dispatch }),
      generateAuthToken: () => generateAuthToken({ agencyPortal, dispatch }),
      updateAgencyPortal: (agencyPortalParams: AgencyPortalModel, requestOptions?: AgencyPortalRequestOptions) => (
        updateAgencyPortal({
          agencyPortal, agencyPortalParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    deleting,
    loading,
    portal,
    updating,
  }
}

export default useAgencyPortal
